import { useEffect, useState, useContext } from 'react';

import { COMMERCE_BACKEND_CCP } from '@atlassian/commerce-ui/rollout';
import type { SubscriptionInEntitlementDetailsV2 } from '@atlassian/commerce-ccp-gateway-client';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useCanAddBillingForTrialProductUser } from '@confluence/experiment-more-product-users-can-upgrade-pay/entry-points/useCanAddBillingForTrialProductUser.ts';

import { getCurrentEntitlementDetails } from './getCurrentEntitlementDetails.ts';
import { buildChangePlanUrl, buildPaymentFlowUrl } from './buildBillingUrls.ts';
import { useCommerceBackendName } from './useCommerceBackendName.ts';
export interface UseBillingUrlsResponse {
	changePlanUrl: string;
	paymentFlowUrl: string;
	loading: boolean;
	error: Error | null;
}

const FALLBACK_BILLING_URL = 'https://admin.atlassian.com/billing';

export const useBillingUrls = (skip: boolean = false): UseBillingUrlsResponse => {
	const { environment, cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { canAddBillingForTrial } = useCanAddBillingForTrialProductUser();
	const isUserEligible = isSiteAdmin || canAddBillingForTrial;

	if (!isUserEligible || !cloudId) {
		skip = true;
	}

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [changePlanUrl, setChangePlanUrl] = useState(FALLBACK_BILLING_URL);
	const [paymentFlowUrl, setPaymentFlowUrl] = useState(FALLBACK_BILLING_URL);

	const commerceBackendData = useCommerceBackendName(skip || !isSiteAdmin);
	const { loading: commerceBackendNameLoading, error: commerceBackendNameError } =
		commerceBackendData;
	let { commerceBackendName } = commerceBackendData;

	if (canAddBillingForTrial) {
		commerceBackendName = COMMERCE_BACKEND_CCP;
	}

	useEffect(() => {
		if (skip) {
			return;
		}
		if (commerceBackendNameError) {
			setError(commerceBackendNameError);
			setLoading(false);
		} else if (!commerceBackendNameLoading && commerceBackendName) {
			const loadBillingDetailsUrl = async () => {
				try {
					let transactionAccountId: string | undefined = '';
					let entitlementId: string | undefined = '';
					let subscriptionv2: SubscriptionInEntitlementDetailsV2 | undefined;
					if (commerceBackendName === COMMERCE_BACKEND_CCP) {
						const result = await getCurrentEntitlementDetails();
						if (result) {
							transactionAccountId = result.transactionAccountId;
							subscriptionv2 = result.subscriptionv2;
							entitlementId = result.entitlementId;
						}
					}
					const invoiceGroupId = subscriptionv2?.accountDetails?.invoiceGroupId ?? '';

					setChangePlanUrl(
						buildChangePlanUrl({
							environment,
							commerceBackendName,
							cloudId,
							entitlementId,
							transactionAccountId,
							invoiceGroupId,
						}),
					);
					setPaymentFlowUrl(
						buildPaymentFlowUrl({
							environment,
							commerceBackendName,
							cloudId,
							entitlementId,
							transactionAccountId,
							invoiceGroupId,
						}),
					);
				} catch (error) {
					error.message = `useBillingUrls: ${error.message}`;
					setError(error);
				} finally {
					setLoading(false);
				}
			};
			void loadBillingDetailsUrl();
		}
	}, [
		commerceBackendNameLoading,
		commerceBackendNameError,
		commerceBackendName,
		cloudId,
		environment,
		skip,
	]);

	return {
		changePlanUrl,
		paymentFlowUrl,
		loading: skip ? false : loading,
		error: skip ? null : error,
	};
};
